/**
 * Line Item Properties Updater
 * 
 * Automatically updates line item properties when product variants change.
 * This is a standalone module that doesn't modify core theme files.
 * 
 * Properties managed:
 * - SKU (Artikelnr) - variant-specific, updated on change
 * - Delivery time (Lieferzeit) - product-level, variant ID tracked
 * - Product info (Produktinfo) - product-level (weight, bean form, expiration), static
 * 
 * Listens for:
 * - variant:changed event (from product-info.js)
 * - variant:change event (custom themes)
 * - Direct variant selector changes (fallback)
 */

(function() {
  'use strict';

  /**
   * Update line item properties for a specific product form
   */
  function updateLineItemProperties(productForm, variant) {
    if (!productForm || !variant) return;

    const productId = variant.product_id || null;
    const variantId = variant.id;

    // Update SKU property (variant-specific)
    const skuInput = productForm.querySelector('input[data-property="sku"]');
    if (skuInput) {
      if (variant.sku) {
        skuInput.value = variant.sku;
        skuInput.dataset.variantId = variantId;
      } else {
        // If no SKU, remove or clear the input
        skuInput.value = '';
      }
    }

    // Update delivery time property if needed
    // Note: Delivery time is typically static per product, but we keep the variant ID updated
    const deliveryInput = productForm.querySelector('input[data-property="delivery"]');
    if (deliveryInput) {
      deliveryInput.dataset.variantId = variantId;
    }

    // Product info (weight, bean form, expiration) is product-level, not variant-level
    // No need to update on variant change as it's static per product
  }

  /**
   * Find all product forms in the document or within a specific container
   */
  function getProductForms(container = document) {
    return container.querySelectorAll('form[data-type="add-to-cart-form"], form[is="product-form"]');
  }

  /**
   * Get variant data from the variant:changed or variant:change event
   */
  function getVariantFromEvent(event) {
    return event.detail?.variant || event.detail?.selectedVariant || null;
  }

  /**
   * Initialize listeners for variant changes
   */
  function init() {
    // Listen for variant:changed event (from product-info.js)
    document.addEventListener('variant:changed', (event) => {
      const variant = getVariantFromEvent(event);
      if (!variant) return;

      // Update all product forms that might be affected
      // Typically this is the main product form, but could include sticky ATC, etc.
      getProductForms().forEach(form => {
        const formVariantInput = form.querySelector('input[name="id"]');
        if (formVariantInput && formVariantInput.value == variant.id) {
          updateLineItemProperties(form, variant);
        }
      });
    });

    // Listen for variant:change event (alternative event name used by some themes)
    document.addEventListener('variant:change', (event) => {
      const variant = getVariantFromEvent(event);
      if (!variant) return;

      getProductForms().forEach(form => {
        const formVariantInput = form.querySelector('input[name="id"]');
        if (formVariantInput && formVariantInput.value == variant.id) {
          updateLineItemProperties(form, variant);
        }
      });
    });

    // Fallback: Listen for direct variant selector changes
    // This handles cases where custom events might not fire
    document.addEventListener('change', (event) => {
      const target = event.target;
      
      // Check if this is a variant selector
      if (target.name === 'id' && (target.tagName === 'SELECT' || target.tagName === 'INPUT')) {
        const form = target.closest('form[data-type="add-to-cart-form"], form[is="product-form"]');
        if (!form) return;

        // Try to get variant data from the selector
        let variantData = null;

        if (target.tagName === 'SELECT') {
          const selectedOption = target.selectedOptions?.[0];
          if (selectedOption) {
            variantData = {
              id: selectedOption.value,
              sku: selectedOption.dataset.sku || '',
              product_id: selectedOption.dataset.productId || null
            };
          }
        } else if (target.tagName === 'INPUT') {
          variantData = {
            id: target.value,
            sku: target.dataset.sku || '',
            product_id: target.dataset.productId || null
          };
        }

        if (variantData && variantData.id) {
          updateLineItemProperties(form, variantData);
        }
      }
    });

    // Handle dynamic content (quick view, etc.)
    // Listen for content being added to the page
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              // Check if the added node or its children contain product forms
              const forms = getProductForms(node);
              if (forms.length > 0) {
                // Forms were added, they already have the correct initial values from Liquid
                // No action needed, but we're ready to handle variant changes
              }
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

